import {
  ActionData,
  Toolbar,
  ToolbarProvider,
} from '@assembly-js/design-system';
import { Editor, EditorContent } from '@tiptap/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { EDITOR_ACTIONS_CONFIG } from './constants';
import { HighlightToolbar } from './highlight-menu/HighlightToolbar';
import { useEditorActions } from './hooks/useEditorActions';
import { LinkSelectionPopover } from './link-menu/LinkSelectionPopover';
import { LinkViewPopover } from './link-menu/LinkViewPopover';

/**
 * RichTextInput component is a wrapper around the tiptap editor that provides a toolbar and highlight menu.
 * @param editor - The tiptap editor instance
 * @param classes - The classes to apply to the container and toolbar
 * @param supportActions - The actions to support in the toolbar and highlight menu
 * @param showTopToolbar - Whether to show the top toolbar
 * @returns
 */
export const RichTextInput = ({
  editor,
  classes,
  supportActions,
  showTopToolbar = true,
  showHighlightToolbar = true,
  children,
  onLinkPopoverOpenChange,
}: {
  editor: Editor;
  classes?: {
    container?: string;
    toolbar?: string;
  };
  supportActions: { toolbar?: string[]; highlight?: string[] };
  showTopToolbar?: boolean;
  showHighlightToolbar?: boolean;
  children?: React.ReactNode;
  onLinkPopoverOpenChange?: (isOpen: boolean) => void;
}) => {
  const { getActionState, onAction } = useEditorActions(editor);

  const [showLinkEditPopover, setShowLinkEditPopover] = useState(false);
  const [activeLinkUrl, setActiveLinkUrl] = useState<string | null>(null);

  // Notify parent when link popover open state changes
  useEffect(() => {
    onLinkPopoverOpenChange?.(showLinkEditPopover);
  }, [showLinkEditPopover, onLinkPopoverOpenChange]);

  // Detect when cursor is on a link
  useEffect(() => {
    const updateLinkState = () => {
      // Don't show view popover if edit popover is open
      if (showLinkEditPopover) {
        setActiveLinkUrl(null);
        return;
      }

      if (editor.isActive('link')) {
        const { href } = editor.getAttributes('link');
        setActiveLinkUrl(href ?? null);
      } else {
        setActiveLinkUrl(null);
      }
    };

    editor.on('selectionUpdate', updateLinkState);
    editor.on('transaction', updateLinkState);

    return () => {
      editor.off('selectionUpdate', updateLinkState);
      editor.off('transaction', updateLinkState);
    };
  }, [editor, showLinkEditPopover]);

  /**
   * Handle the action formatting or if the action is
   * a non formatting action such as link, it handles
   * showing the link popover.
   * @param actionData - The action data to handle
   */
  const handleAction = (actionData: ActionData) => {
    if (actionData.action === 'link') {
      setActiveLinkUrl(null); // Hide view popover
      setShowLinkEditPopover(true);
    } else {
      onAction(actionData);
    }
  };

  const handleEditLink = () => {
    setActiveLinkUrl(null);
    setShowLinkEditPopover(true);
  };

  return (
    <ToolbarProvider
      config={EDITOR_ACTIONS_CONFIG}
      onAction={handleAction}
      getActionState={getActionState}
    >
      {showHighlightToolbar && (
        <HighlightToolbar
          tiptapEditor={editor}
          overrideActions={supportActions.highlight}
        />
      )}
      {showTopToolbar && (
        <Toolbar
          variant="sticky"
          overrideActions={supportActions.toolbar}
          className={clsx(
            classes?.toolbar,
            'w-[-webkit-fill-available] sticky top-0 right-0 z-10',
          )}
        />
      )}
      <div className={classes?.container}>
        {children}
        <EditorContent editor={editor} />
      </div>
      <LinkSelectionPopover
        editor={editor}
        isOpen={showLinkEditPopover}
        onClose={() => setShowLinkEditPopover(false)}
      />
      {activeLinkUrl && (
        <LinkViewPopover
          editor={editor}
          linkUrl={activeLinkUrl}
          onEdit={handleEditLink}
          onClose={() => setActiveLinkUrl(null)}
        />
      )}
    </ToolbarProvider>
  );
};
